import type { ReactNode } from 'react';
import { Card, CardBody } from '@/components/ui';

export interface SummaryStat {
  label: string;
  value: string;
  hint?: string;
  /** Draws the figure in red when it is money the business is owed or owes. */
  tone?: 'default' | 'owed';
}

/**
 * The figures above a list.
 *
 * Every one of these describes the whole filter, never the page on screen: a
 * total that changed as someone paged through would teach a business not to
 * trust its own screens.
 */
export function SummaryCards({ stats }: { stats: (SummaryStat | null)[] }) {
  const shown = stats.filter((stat): stat is SummaryStat => stat !== null);
  if (shown.length === 0) return null;

  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {shown.map((stat) => (
        <Card key={stat.label}>
          <CardBody>
            <p className="text-sm text-muted">{stat.label}</p>
            <p
              className={`mt-1 text-2xl font-semibold tabular ${
                stat.tone === 'owed' && Number(stat.value.replace(/[^0-9.-]/g, '')) > 0
                  ? 'text-red-600 dark:text-red-400'
                  : ''
              }`}
            >
              {stat.value}
            </p>
            {stat.hint && <p className="mt-1 text-xs text-muted">{stat.hint}</p>}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

/** Wraps a page's filter row so every money screen lays them out the same. */
export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">{children}</div>;
}
